// First-party traffic-source capture (ACEB-241): each hard landing stores where
// the visitor came from so checkout can send it with the order. Same lifecycle
// as lib/clickIds.ts; the why and the rules live in
// docs/features/analytics-tracking.md ("Traffic source").

import {
  AttributionClickId,
  type OrderAttributionInput,
  type OrderAttributionTouch,
} from '../types/order';
import { getCanonicalPath } from '../i18n/pathnames';
import { defaultLocale, locales, type Locale } from '../i18n/config';

const STORAGE_KEY = 'aceb_attribution';
const DAY_MS = 24 * 60 * 60 * 1000;
// Last-non-direct-touch lookback: a buyer whose last external visit is older
// than this came back on their own.
const LAST_TOUCH_TTL_MS = 30 * DAY_MS;
const FIRST_TOUCH_TTL_MS = 365 * DAY_MS;
// A stored timestamp further in the future than this is a clock or tamper artefact.
const CLOCK_SKEW_MS = 60 * 1000;
// Kept under the API's DTO limits so a stale stash can never fail a checkout.
const MAX_VALUE_LENGTH = 200;
const MAX_PATH_LENGTH = 512;

const TEXT_FIELDS = ['referrerDomain', 'utmSource', 'utmMedium', 'utmCampaign'] as const;
type TextField = (typeof TEXT_FIELDS)[number];

const UTM_PARAMS: ReadonlyArray<readonly [TextField, string]> = [
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
];
const CLICK_ID_PARAMS = Object.values(AttributionClickId);
// A friend's referral link; lib/referral.ts owns the code itself.
const REFERRAL_PARAM = 'ref';

// Round-trips, not visits: pages a payment / identity / OAuth provider or one of
// our transactional emails sends people back to. Their referrer is the provider
// or a mail client, never the buyer's source, so it is dropped; campaign params
// on the same URL still count. Canonical (English) paths; the locale prefix and
// translated slugs are resolved before matching.
const PASS_THROUGH_PATHS = [
  '/order',
  '/account/verification',
  '/google',
  '/facebook',
  '/github',
  '/twitter',
  '/confirm-email',
  '/reset-password',
  '/newsletter',
];
const passThroughRe = new RegExp(`^(?:${PASS_THROUGH_PATHS.join('|')})(?:/|$)`);

// Second layer for providers whose return page is not fixed: hosts that are
// never a real source (any subdomain matches). Content sites people also share
// links from (facebook.com, github.com) do not belong here; their OAuth
// callbacks are covered by the path list above.
const PROVIDER_REFERRER_HOSTS = [
  'coingate.com',
  'triple-a.io',
  'ondato.com',
  'accounts.google.com',
  'appleid.apple.com',
  'login.microsoftonline.com',
  'login.live.com',
  'api.twitter.com',
  'api.x.com',
];

const isProviderHost = (host: string): boolean =>
  PROVIDER_REFERRER_HOSTS.some((provider) => host === provider || host.endsWith(`.${provider}`));

interface Stored {
  first?: OrderAttributionTouch;
  last?: OrderAttributionTouch;
}

const isClickId = (value: unknown): value is AttributionClickId =>
  typeof value === 'string' && (CLICK_ID_PARAMS as string[]).includes(value);

const trimmedText = (value: unknown, max: number): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : undefined;

const isStale = (capturedAt: number, now: number, ttlMs: number): boolean =>
  now - capturedAt > ttlMs || capturedAt > now + CLOCK_SKEW_MS;

// Any external signal at all; a landing without one is a direct visit.
const isNonDirect = (touch: OrderAttributionTouch): boolean =>
  TEXT_FIELDS.some((field) => !!touch[field]) || !!touch.clickId || !!touch.referralLink;

// Re-validates a stored touch so a stale or hand-edited value can only shrink
// to nothing, never reach the API malformed.
function sanitizeTouch(
  value: unknown,
  now: number,
  ttlMs: number,
): OrderAttributionTouch | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const capturedAt = typeof raw.capturedAt === 'number' && Number.isFinite(raw.capturedAt)
    ? raw.capturedAt
    : undefined;
  const landingPath = trimmedText(raw.landingPath, MAX_PATH_LENGTH);
  if (capturedAt === undefined || !landingPath || isStale(capturedAt, now, ttlMs)) {
    return undefined;
  }
  const touch: OrderAttributionTouch = { landingPath, capturedAt };
  for (const field of TEXT_FIELDS) {
    const text = trimmedText(raw[field], MAX_VALUE_LENGTH);
    if (text) touch[field] = text;
  }
  // A stash written before a provider host was listed still names it.
  if (touch.referrerDomain && isProviderHost(touch.referrerDomain.toLowerCase())) {
    delete touch.referrerDomain;
  }
  if (isClickId(raw.clickId)) touch.clickId = raw.clickId;
  if (raw.referralLink === true) touch.referralLink = true;
  return touch;
}

function read(now: number): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const first = sanitizeTouch(parsed.first, now, FIRST_TOUCH_TTL_MS);
    const last = sanitizeTouch(parsed.last, now, LAST_TOUCH_TTL_MS);
    // The last touch is non-direct by definition; one left empty is dropped.
    return { ...(first && { first }), ...(last && isNonDirect(last) && { last }) };
  } catch {
    return {};
  }
}

function write(stored: Stored): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // storage unavailable (private mode, quota) - checkout sends nothing
  }
}

// Strips the locale prefix and translated slug so pass-through routes match
// on their canonical path in every locale.
function canonicalPathOf(pathname: string): string {
  const [, firstSegment = '', ...rest] = pathname.split('/');
  const isLocale = (locales as readonly string[]).includes(firstSegment);
  const locale: Locale = isLocale ? (firstSegment as Locale) : defaultLocale;
  const localizedPath = isLocale ? `/${rest.join('/')}` : pathname;
  return getCanonicalPath(localizedPath, locale);
}

// The referrer's host when it is another site; own hostnames (any subdomain
// of the apex, so locale/admin subdomains too) are internal navigation.
function externalReferrerDomain(referrer: string, ownHost: string): string | undefined {
  if (!referrer) return undefined;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return undefined;
  }
  if (!host) return undefined;
  const own = ownHost.toLowerCase();
  const apex = own.split('.').slice(-2).join('.');
  if (host === own || host === apex || host.endsWith(`.${apex}`)) return undefined;
  return host.replace(/^www\./, '').slice(0, MAX_VALUE_LENGTH);
}

function buildTouch(
  location: Location,
  referrer: string,
  now: number,
): OrderAttributionTouch | undefined {
  const params = new URLSearchParams(location.search);
  const touch: OrderAttributionTouch = {
    landingPath: location.pathname.slice(0, MAX_PATH_LENGTH),
    capturedAt: now,
  };
  const referrerDomain = externalReferrerDomain(referrer, location.hostname);
  const roundTrip = passThroughRe.test(canonicalPathOf(location.pathname))
    || (!!referrerDomain && isProviderHost(referrerDomain));
  if (referrerDomain && !roundTrip) touch.referrerDomain = referrerDomain;
  for (const [field, param] of UTM_PARAMS) {
    const text = trimmedText(params.get(param), MAX_VALUE_LENGTH);
    if (text) touch[field] = text;
  }
  const clickId = CLICK_ID_PARAMS.find((name) => !!params.get(name));
  if (clickId) touch.clickId = clickId;
  // Shares over messengers arrive with no referrer; this flag keeps them visible.
  if (params.get(REFERRAL_PARAM)?.trim()) touch.referralLink = true;
  // A provider round-trip without campaign params is not a landing at all.
  if (roundTrip && !isNonDirect(touch)) return undefined;
  return touch;
}

// Back/forward and reload replay a page with its first referrer but the current
// URL (browser Back from the payment page into /cart); that is not a new landing.
function isHistoryNavigation(): boolean {
  try {
    const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return entry?.type === 'back_forward' || entry?.type === 'reload';
  } catch {
    return false;
  }
}

// Runs once per hard navigation (Providers mount). The first landing ever seen
// is kept as-is, direct or not; every non-direct landing replaces the last touch.
export function captureAttributionFromLanding(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  const stored = read(now);
  // A replayed page may fill an empty stash but never replaces a touch.
  if (isHistoryNavigation() && (stored.first || stored.last)) return;
  const touch = buildTouch(window.location, document.referrer, now);
  if (!touch) return;
  const next: Stored = {
    first: stored.first ?? touch,
    ...(stored.last && { last: stored.last }),
  };
  if (isNonDirect(touch)) next.last = touch;
  write(next);
}

/** What checkout sends with the order; undefined when nothing was captured. */
export function getStoredAttribution(): OrderAttributionInput | undefined {
  if (typeof window === 'undefined') return undefined;
  const stored = read(Date.now());
  return stored.first || stored.last ? stored : undefined;
}

/** Mirrors the click-id wipe on consent withdrawal. */
export function clearStoredAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}
